import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { ShipmentSmsService } from '../../../services/shipment-sms.service';
import {
  ShipmentSmsEngagement,
  ShipmentSmsSummary,
  SmsStatus
} from '../../models/shipment-sms';

const PAGE_SIZE = 25;

/**
 * What the SMS channel is actually doing.
 *
 * Built around one question rather than a list: we pay per segment, so the only thing worth knowing
 * is how far a message gets. Sent, opened, asked to join — three numbers, in that order, and a table
 * underneath for the rows that need chasing.
 *
 * The failed rows are the actionable ones: each is a phone number somebody can go and correct in
 * partner settings, after which the courier can send again from the shipment.
 */
@Component({
  selector: 'app-shipment-sms',
  templateUrl: './shipment-sms.component.html',
  styleUrls: ['./shipment-sms.component.scss']
})
export class ShipmentSmsComponent implements OnInit, OnDestroy {

  loading = true;
  error: string | null = null;

  summary: ShipmentSmsSummary | null = null;
  rows: ShipmentSmsEngagement[] = [];

  statusFilter: SmsStatus | '' = '';
  joinOnly = false;
  /** The shortlist worth a person's time: readers who replaced the address we hold. */
  overriddenOnly = false;

  page = 0;
  total = 0;
  readonly pageSize = PAGE_SIZE;

  readonly statusOptions: { value: SmsStatus | ''; label: string }[] = [
    { value: '', label: 'All statuses' },
    { value: 'SENT', label: 'Sent' },
    { value: 'PENDING', label: 'Sending' },
    { value: 'FAILED', label: 'Failed' }
  ];

  private readonly destroy$ = new Subject<void>();

  constructor(private readonly smsService: ShipmentSmsService) {}

  ngOnInit(): void {
    this.loadSummary();
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Filters go back to the server, because they narrow a paged result rather than an array. */
  onFilterChange(): void {
    this.page = 0;
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;

    this.smsService.getEngagement({
      page: this.page,
      size: this.pageSize,
      status: this.statusFilter,
      joinRequested: this.joinOnly,
      emailOverridden: this.overriddenOnly
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.rows = response.data ?? [];
          this.total = response.total ?? 0;
          this.loading = false;
        },
        error: () => {
          this.error = 'Could not load SMS activity.';
          this.rows = [];
          this.loading = false;
        }
      });
  }

  private loadSummary(): void {
    this.smsService.getSummary()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: summary => this.summary = summary,
        // The table is the point; a missing summary should not blank the page.
        error: () => this.summary = null
      });
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages || page === this.page) {
      return;
    }
    this.page = page;
    this.load();
  }

  /**
   * How far this message got, as one word.
   *
   * Ordered by how far down the funnel it is, so the strongest true statement wins: a link that
   * produced a lead is reported as such even though it was also opened and also sent.
   */
  outcome(row: ShipmentSmsEngagement): 'override' | 'lead' | 'opened' | 'delivered' | 'sending' | 'failed' {
    if (row.status === 'FAILED') return 'failed';
    // Ahead of 'lead' deliberately. An override is a lead, but it is the one that needs looking at,
    // and reporting it as an ordinary lead is how it would go unnoticed in a list sorted by date.
    if (row.joinEmailOverridden) return 'override';
    if (row.joinRequestedAt) return 'lead';
    if (row.openedAt) return 'opened';
    if (row.status === 'PENDING') return 'sending';
    return 'delivered';
  }

  outcomeLabel(row: ShipmentSmsEngagement): string {
    switch (this.outcome(row)) {
      case 'override': return 'Different address';
      case 'lead': return 'Asked to join';
      case 'opened': return 'Opened';
      case 'delivered': return 'Sent';
      case 'sending': return 'Sending';
      case 'failed': return 'Failed';
    }
  }

  sideLabel(row: ShipmentSmsEngagement): string {
    switch (row.recipientSide) {
      case 'SENDER': return 'Sender';
      case 'RECEIVER': return 'Recipient';
      default: return 'Courier';
    }
  }

  /** Share of issued links that were opened at least once, as a whole percentage. */
  get openRate(): number | null {
    if (!this.summary || this.summary.linksIssued === 0) return null;
    return Math.round((this.summary.opened / this.summary.linksIssued) * 100);
  }

  /**
   * Leads as a share of the links that were actually opened, not of everything sent.
   *
   * Somebody who never opened the message was never asked, so counting them in the denominator
   * would make the call to action look worse than it is and hide a delivery problem as a
   * conversion problem.
   */
  get joinRate(): number | null {
    if (!this.summary || this.summary.opened === 0) return null;
    return Math.round((this.summary.joinRequested / this.summary.opened) * 100);
  }
}
